import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import request from "src/utils/request";
import loaderGif from "../common/no_bg_loader.gif";

const MailPreview = ({
  showPreview,
  setShowPreview,
  sendMultiMail,
  endorsementType = "ShareEndorsement"
}) => {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<string | null>(null);

  // 🔑 iframe ref (printing happens here)
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (showPreview?.status) {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview?.status]);

  const fetchPreview = () => {
    setPreviewLoading(true);
    request({
      url: "endorsement/getEndorsementMailPreview",
      method: "post",
      data: {
        ids: showPreview.checkedItems,
        type: endorsementType,
        insuranceCompanyId: showPreview.insuranceCompanyValue,
        insuranceCompanyBranchId: showPreview.insuranceCompanyBranchValue,
        insuranceCompanyBranchContact:
          showPreview?.branchContactValue?.id ?? null,
        otherContact: showPreview?.customEmail ?? null,
        showButton: false
      }
    }).then(
      (response: any) => {
        setPreviewData(response);
        setPreviewLoading(false);
      },
      (error: any) => {
        console.error(error);
        setPreviewLoading(false);
      }
    );
  };

  // ✅ WORKING PRINT (same reliability as EndorsementView)
  const handlePrint = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const iframeWindow = iframe.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.focus();
    iframeWindow.print();
  };

  const handleClose = () => {
    setShowPreview({
      status: false,
      checkedItems: null,
      endorsementType: null,
      insuranceCompanyValue: null,
      insuranceCompanyBranchValue: null,
      branchContactValue: null,
      customEmail: null
    });
  };

  const emailSource = showPreview?.customEmail || showPreview?.branchContactValue?.email || "";

  const emailList = emailSource
    .split(",")
    .map((e: string) => e.trim())
    .filter(Boolean);

  const toEmail = emailList[0] || "";
  const ccEmails = emailList.slice(1).join(", ");

  return (
    <Dialog
      open={showPreview.status}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      {/* ================= HEADER ================= */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Mail Preview
        </Typography>

        <IconButton color="inherit" onClick={handlePrint}>
          <PrintIcon />
        </IconButton>
      </DialogTitle>

      {/* ================= CONTENT ================= */}
      <DialogContent sx={{ p: 2 }}>
        {previewLoading ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <img
                      alt="loader"
                      src={loaderGif}
                      width={100}
                      height={100}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        ) : (
          <Box>
            <Typography variant="subtitle1" fontWeight={500} mb={1}>
              Mail To: {toEmail}
            </Typography>

            {ccEmails && (
              <Typography variant="subtitle1" fontWeight={500} mb={1}>
                CC: {ccEmails}
              </Typography>
            )}

            {/* ================= IFRAME PREVIEW ================= */}
            <iframe
              ref={iframeRef}
              title="Mail HTML Preview"
              srcDoc={previewData || ""}
              style={{
                display: "block",
                width: "100%",
                height: "100vh",
                border: "none"
              }}
            />
          </Box>
        )}
      </DialogContent>

      {/* ================= ACTIONS ================= */}
      <DialogActions>
        <Button
          onClick={() => sendMultiMail(showPreview.checkedItems)}
          variant="contained"
          color="primary"
        >
          Submit
        </Button>

        <Button
          onClick={handleClose}
          variant="contained"
          color="error"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MailPreview;
